import { type ReactNode, createContext, useCallback, useContext } from 'react';

import { isResourceName } from './resourceNames';
import { useResourceDisplayNames } from './useResourceDisplayNames';

type ResolveResourceName = (name: string) => string;

const defaultResolve: ResolveResourceName = (name: string) => name;

const ResourceDisplayNameContext =
  createContext<ResolveResourceName>(defaultResolve);
const ResourceDisplayNamesLoadingContext = createContext(false);

export const ResourceDisplayNameProvider = ({
  input,
  additionalNames,
  children,
}: {
  input: unknown;
  additionalNames?: string[];
  children: ReactNode;
}) => {
  const { resolvedNames, loading } = useResourceDisplayNames(
    input,
    additionalNames,
  );

  const resolve: ResolveResourceName = useCallback(
    (name: string) => {
      if (!isResourceName(name)) return name;
      return resolvedNames.get(name) || name;
    },
    [resolvedNames],
  );

  return (
    <ResourceDisplayNamesLoadingContext.Provider value={loading}>
      <ResourceDisplayNameContext.Provider value={resolve}>
        {children}
      </ResourceDisplayNameContext.Provider>
    </ResourceDisplayNamesLoadingContext.Provider>
  );
};

export const useResolveResourceName = (): ResolveResourceName =>
  useContext(ResourceDisplayNameContext);

export const useResourceDisplayNamesLoading = (): boolean =>
  useContext(ResourceDisplayNamesLoadingContext);
